import * as SliderPrimitive from "@radix-ui/react-slider";
import { useLayoutEffect, useRef } from "react";

import { cn } from "@/web/lib/styling";
import { useActionCardCategoryOptional } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type { SmbActionCategory } from "@/web/routes/_protected/smb/-dashboard/-types";

function defaultFormat(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    const m = value / 1_000_000;
    const rounded = Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1);
    return `$${rounded}M`;
  }
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value}`;
}

/**
 * Where the slider is rendered, which determines its color treatment:
 *   - `on-color`: sits on a colored card background (the original use). Track,
 *     range, and thumb are white; the value bubble is a white chip with
 *     category-colored text.
 *   - `on-surface`: sits on a plain light surface (e.g. a drawer). Track is a
 *     muted line, range + thumb take the category accent color, and the bubble
 *     is a bordered chip — so it's legible without a colored backdrop. This is
 *     what keeps the slider independent of the ActionCard, visually.
 */
type BudgetSliderTone = "on-color" | "on-surface";

const BUBBLE_FG: Record<SmbActionCategory, string> = {
  negotiate: "text-withus-red",
  budget: "text-withus-blue",
  fundraise: "text-withus-blue",
  generic: "text-withus-white",
};

// Accent (range fill + thumb) for the `on-surface` tone, per category.
const ACCENT_BG: Record<SmbActionCategory, string> = {
  negotiate: "bg-withus-red",
  budget: "bg-withus-blue",
  fundraise: "bg-withus-blue",
  generic: "bg-withus-dark-blue",
};

/**
 * Range slider for dollar amounts. Self-contained: it can read its accent
 * category from a surrounding `<ActionCard />` but does not require one — pass
 * `category` (and `tone="on-surface"`) to render it anywhere, e.g. a drawer.
 */
export function BudgetSlider({
  value,
  onChange,
  min,
  max,
  step = 1000,
  format = defaultFormat,
  className,
  category: categoryProp,
  tone = "on-color",
  disabled = false,
}: {
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  step?: number;
  format?: (value: number) => string;
  className?: string;
  /**
   * Accent category. Defaults to the surrounding `<ActionCard />`'s category;
   * pass explicitly when rendering outside a card (there's no context to read).
   */
  category?: SmbActionCategory;
  /** Color treatment — see {@link BudgetSliderTone}. Defaults to `on-color`. */
  tone?: BudgetSliderTone;
  /** Renders the slider muted and non-interactive. */
  disabled?: boolean;
}) {
  const contextCategory = useActionCardCategoryOptional();
  // Fall back to "negotiate" if neither a prop nor a card context is available,
  // so a standalone slider still gets a sensible accent color.
  const category = categoryProp ?? contextCategory ?? "negotiate";
  const percent = max === min ? 0 : ((value - min) / (max - min)) * 100;
  const trackRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    function update() {
      if (!labelRef.current || !trackRef.current) return;
      const trackWidth = trackRef.current.offsetWidth;
      const labelWidth = labelRef.current.offsetWidth;
      const halfLabel = labelWidth / 2;
      const thumbX = (percent / 100) * trackWidth;
      const clampedCenter = Math.max(halfLabel, Math.min(trackWidth - halfLabel, thumbX));
      const offsetPx = clampedCenter - thumbX;
      labelRef.current.style.transform = `translateX(calc(-50% + ${offsetPx}px))`;
    }

    update();

    const observer = new ResizeObserver(update);
    if (trackRef.current) observer.observe(trackRef.current);
    if (labelRef.current) observer.observe(labelRef.current);

    return () => observer.disconnect();
  }, [percent]);

  const onColor = tone === "on-color";

  return (
    <div
      ref={trackRef}
      // `data-vaul-no-drag` keeps a surrounding vaul Drawer from treating a
      // thumb drag as a drawer drag-to-dismiss gesture (the slider lives inside
      // the budget drawer).
      data-vaul-no-drag
      className={cn(
        "relative w-full pt-14 pb-5",
        disabled && "pointer-events-none opacity-40",
        className,
      )}
    >
      <div
        ref={labelRef}
        className="pointer-events-none absolute top-4 bg-withus-white px-1.5 py-0.5"
        style={{ left: `${percent}%` }}
      >
        <span className={cn("withus-strong text-base", BUBBLE_FG[category])}>{format(value)}</span>
      </div>
      <SliderPrimitive.Root
        className="relative flex w-full touch-none items-center select-none"
        value={[value]}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onValueChange={([next]) => {
          if (next != null) onChange(next);
        }}
      >
        <SliderPrimitive.Track
          className={cn(
            "relative h-1 w-full grow",
            onColor ? "bg-withus-white/10" : "bg-withus-dark-blue/15",
          )}
        >
          <SliderPrimitive.Range
            className={cn("absolute h-full", onColor ? "bg-withus-white" : ACCENT_BG[category])}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className={cn(
            "block size-5 rounded-full shadow-withus-default focus-visible:ring-2 focus-visible:outline-none",
            onColor
              ? "bg-withus-white focus-visible:ring-withus-white/40"
              : cn(ACCENT_BG[category], "focus-visible:ring-withus-dark-blue/30"),
          )}
          aria-label="Value"
        />
      </SliderPrimitive.Root>
      <div className="mt-3 flex justify-between text-xs opacity-50">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}
